import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { TextareaField } from '@/components/TextareaField';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { usePostNewsMutation } from '@/services/queries/news';
import { PostNewsCalendarNewsRequest } from '@/services/request/news';
import { useTranslation } from 'react-i18next';
import dayjs from '@/utils/dayjs.config';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface NewsCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const NewsCreateModal = ({ isOpen, onClose, onDone }: NewsCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostNewsMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      file: undefined,
      content: '',
      source_url: '',
      publish_at: '',
      is_draft: false,
      is_scheduled: false,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const publishAt =
        data.is_draft || !data.publish_at || !dayjs(data.publish_at).isValid() ? '' : dayjs(data.publish_at).toISOString();
      const payload: PostNewsCalendarNewsRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.file,
        title: data.title,
        content: data.content,
        source_url: data.source_url,
        is_draft: data.is_draft,
        publish_at: publishAt,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_news.components.news_create_modal.news_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_news.components.news_create_modal.news_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_news.components.news_create_modal.news_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_news.components.news_create_modal.news_create_label_team')}
              placeholder={t('pages.application_news.components.news_create_modal.news_create_label_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_news.components.news_create_modal.news_create_label')}
              {...register('title')}
              helperText={t('pages.application_news.components.news_create_modal.news_create_helper_text')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.content}>
              <Text fontWeight='medium'>{t('pages.application_news.components.news_create_modal.news_create_label_text')}</Text>

              <TextareaField
                maxLength={4000}
                helperText={t('pages.application_news.components.news_create_modal.news_create_helper_text_label')}
                error={errors?.content?.message}
                {...register('content')}
              />
            </FormControl>

            <InputField
              withoutFloating
              label={t('pages.application_news.components.news_create_modal.news_create_label_url')}
              {...register('source_url')}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_news.components.news_create_modal.news_create_label_image')}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_news.components.news_create_modal.news_create_help_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            {formValues.is_scheduled && (
              <InputField
                withoutFloating
                label={t('pages.application_news.components.news_create_modal.news_create_date_label')}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                {...register('publish_at')}
                rootProps={{ isInvalid: !!errors?.publish_at, errorMessage: errors?.publish_at?.message }}
              />
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => {
                setValue('is_draft', true);
                handleConfirm();
              }}
              width={{ base: '100%', md: 'initial' }}
              p={0}
              color='brand.500'
              backgroundColor='transparent'
              textDecoration='underline'
              _hover={{
                backgroundColor: 'transparent',
                color: 'brand.500',
              }}
            >
              <ChakraFeatherIcon name='save' />
              {t('pages.application_news.components.news_create_modal.news_create_save_draft')}
            </Button>

            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_news.components.news_create_modal.news_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_news.components.news_create_modal.news_create_create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
